import React, { useState, useEffect } from 'react';
import '../../../App.scss';
import 'antd/dist/antd.css';
import { PageHeader, Table, Button, Space, DatePicker, Tag, Avatar } from 'antd';
import { UserOutlined, TeamOutlined, CalendarOutlined } from '@ant-design/icons';
import moment from 'moment';

import { useTeamJoins } from '../../../models/useTeamJoins';
import { ClientsSearch } from '../../../components/admin/AdminClientsComponents';
import CenteredSpinner from '../../../components/CenteredSpinner';
import { getAdminDatePresets, getCurrentWeekRange } from '../../../utils/adminDatePresets';
import { getAppBaseUrl } from '../../../utils/URLBuilder';

const { RangePicker } = DatePicker;
const LIMIT = 400;

const URLPath = getAppBaseUrl();

function escapeCsvValue(value) {
    const text = value === null || value === undefined ? '' : `${value}`;
    if (text.includes('"') || text.includes(',') || text.includes('\n')) {
        return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
}

function toIsoString(ts) {
    if (!ts) return '';
    if (typeof ts.toDate === 'function') return ts.toDate().toISOString();
    if (typeof ts.toMillis === 'function') return new Date(ts.toMillis()).toISOString();
    if (ts instanceof Date) return ts.toISOString();
    return '';
}

const ROLE_COLORS = { owner: 'red', admin: 'gold', member: 'blue', manager: 'purple' };
const TIP_BEHAVIOUR_COLORS = { both: 'blue', members: 'green', team: 'purple', pooled: 'purple' };

export default function AdminTeamJoinsScreen({ locStrings, isAdmin }) {
    const [dateRange, setDateRange] = useState(getCurrentWeekRange);
    const [, setPresetRefresh] = useState(0);
    const startDate = dateRange && dateRange[0] ? dateRange[0].toDate() : null;
    const endDate = dateRange && dateRange[1] ? dateRange[1].toDate() : null;

    const [loading, joins] = useTeamJoins({ startDate, endDate, limit: LIMIT });
    const [searchText, setSearchText] = useState('');
    const [filteredJoins, setFilteredJoins] = useState(null);

    useEffect(() => {
        const words = (searchText || '').toLowerCase().trim().split(/\s+/).filter(Boolean);
        if (words.length === 0) {
            setFilteredJoins(null);
            return;
        }
        const filtered = (joins || []).filter((join) => {
            const rolesStr = Array.isArray(join.roles) ? join.roles.join(' ') : '';
            const isOwner = join.clientId && join.teamOwnerClientId && join.clientId === join.teamOwnerClientId;
            const ownerStr = isOwner ? 'owner' : '';
            const tapLabel = join.teamTapBehaviour === 'team' ? 'pooled' : join.teamTapBehaviour;
            const haystack = [
                join.clientId,
                join.memberId,
                join.teamId,
                join.teamName,
                join.displayName,
                join.email,
                join.status,
                join.teamNumberOfMembers,
                join.teamTapBehaviour,
                tapLabel,
                rolesStr,
                ownerStr,
            ].filter(Boolean).join(' ').toLowerCase();
            return words.every((w) => haystack.includes(w));
        });
        setFilteredJoins(filtered);
    }, [searchText, joins]);

    const onSearch = (value) => setSearchText(value || '');
    const onDateRangeChange = (range) => setDateRange(range && range[0] && range[1] ? range : getCurrentWeekRange());

    const exportCsv = () => {
        const exportList = filteredJoins || joins || [];
        const getRoleStr = (join) => {
            const roles = [...(Array.isArray(join.roles) ? join.roles : [])];
            const isOwner = join.clientId && join.teamOwnerClientId && join.clientId === join.teamOwnerClientId;
            if (isOwner && !roles.some((r) => (r || '').toLowerCase() === 'owner')) {
                roles.unshift('owner');
            }
            return roles.join(',');
        };
        const rows = [
            ['id', 'memberId', 'clientId', 'displayName', 'email', 'avatar', 'teamId', 'teamName', 'teamOwnerClientId', 'teamTopLogo', 'teamNumberOfMembers', 'teamTapBehaviour', 'role', 'status', 'joinedAt', 'updatedAt'],
            ...exportList.map((join) => [
                join.id || '',
                join.memberId || join.id || '',
                join.clientId || '',
                join.displayName || '',
                join.email || '',
                join.avatar || '',
                join.teamId || '',
                join.teamName || '',
                join.teamOwnerClientId || '',
                join.teamTopLogo || '',
                join.teamNumberOfMembers ?? '',
                join.teamTapBehaviour || '',
                getRoleStr(join),
                join.status || '',
                toIsoString(join.createdAt),
                toIsoString(join.updatedAt),
            ]),
        ];
        const csv = rows.map((row) => row.map(escapeCsvValue).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `admin-team-joins-${moment().format('YYYYMMDD-HHmmss')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const displayList = filteredJoins || joins || [];
    const hasRows = displayList.length > 0;
    const resultCount = displayList.length;
    const atLimit = resultCount >= LIMIT;

    const columns = [
        {
            title: 'Member',
            key: 'member',
            render: (_, join) => {
                const clientHref = `${URLPath}/admin/clients/${join.clientId}`;
                const displayName = join.displayName || join.email || join.clientId;
                const isOwner = join.clientId && join.teamOwnerClientId && join.clientId === join.teamOwnerClientId;
                const rolesToShow = [...(Array.isArray(join.roles) ? join.roles : [])];
                if (isOwner && !rolesToShow.some((r) => (r || '').toLowerCase() === 'owner')) {
                    rolesToShow.unshift('owner');
                }
                const roleTags = rolesToShow.length > 0
                    ? rolesToShow.map((r) => (
                        <Tag key={r} color={ROLE_COLORS[(r || '').toLowerCase()] || 'default'}>
                            {r}
                        </Tag>
                    ))
                    : null;
                const joinedLabel = join.createdAt
                    ? moment(join.createdAt.toMillis ? join.createdAt.toMillis() : join.createdAt).format('YYYY-MM-DD HH:mm')
                    : '-';
                return (
                    <Space direction="vertical" size={2}>
                        <Space wrap>
                            <Avatar
                                src={join.avatar}
                                icon={<UserOutlined />}
                                size={32}
                                style={{ flexShrink: 0 }}
                            />
                            <a href={clientHref}>{displayName}</a>
                            {roleTags}
                        </Space>
                        {join.email && join.email !== displayName && (
                            <span style={{ fontSize: 12, color: '#666', marginLeft: 40 }}>{join.email}</span>
                        )}
                        <Space size={6} style={{ fontSize: 12, marginLeft: 40 }}>
                            <CalendarOutlined />
                            <span>{joinedLabel}</span>
                        </Space>
                    </Space>
                );
            },
        },
        {
            title: 'Team',
            key: 'team',
            render: (_, join) => {
                if (!join.teamId) return '-';
                const teamPageUrl = join.teamOwnerClientId
                    ? `${URLPath}/admin/clients/${join.teamOwnerClientId}/teams/${join.teamId}`
                    : null;
                const displayName = join.teamName || join.teamId;
                const memberCount = join.teamNumberOfMembers != null ? join.teamNumberOfMembers : null;
                const tapBehaviour = join.teamTapBehaviour || null;
                const tapLabel = tapBehaviour === 'team' ? 'pooled' : tapBehaviour;
                const tapColor = TIP_BEHAVIOUR_COLORS[tapBehaviour?.toLowerCase()] || TIP_BEHAVIOUR_COLORS[tapLabel?.toLowerCase()] || 'default';
                return (
                    <Space direction="vertical" size={2}>
                        <Space>
                            <Avatar
                                src={join.teamTopLogo}
                                icon={<TeamOutlined />}
                                shape="square"
                                size={32}
                                style={{ borderRadius: 6, flexShrink: 0 }}
                            />
                            {teamPageUrl ? (
                                <a href={teamPageUrl}>{displayName}</a>
                            ) : (
                                <span>{displayName}</span>
                            )}
                        </Space>
                        <Space size={4} style={{ fontSize: 12 }}>
                            {memberCount != null && (
                                <span>{memberCount} member{memberCount !== 1 ? 's' : ''}</span>
                            )}
                            {tapLabel && (
                                <Tag color={tapColor} style={{ marginRight: 0 }}>{tapLabel}</Tag>
                            )}
                        </Space>
                    </Space>
                );
            },
        },
    ];

    if (!isAdmin) {
        return <div>You are not authorized to view this page.</div>;
    }

    return (
        <div style={{ height: '100%', textAlign: 'center' }}>
            <PageHeader ghost={false} title="Team Joins" />
            <Space direction="vertical" style={{ width: '100%', padding: 16 }}>
                <ClientsSearch initialValue={searchText} callbackOnSearch={onSearch} />
                <Space wrap>
                    <RangePicker
                        value={dateRange}
                        onChange={onDateRangeChange}
                        onOpenChange={(open) => { if (open) setPresetRefresh((t) => t + 1); }}
                        ranges={getAdminDatePresets()}
                        allowClear={false}
                    />
                    <span style={{ color: atLimit ? '#ff4d4f' : undefined }}>
                        {resultCount} result{resultCount !== 1 ? 's' : ''} (max {LIMIT})
                    </span>
                    <Button onClick={exportCsv} disabled={!hasRows}>Export CSV</Button>
                </Space>
                {loading ? (
                    <CenteredSpinner loading={true} />
                ) : (
                    <Table
                        dataSource={displayList}
                        columns={columns}
                        rowKey={(r) => `${r.teamId}-${r.memberId || r.clientId}`}
                        pagination={{ pageSize: 50 }}
                        scroll={{ x: 700 }}
                    />
                )}
            </Space>
        </div>
    );
}
